import { copy_to_clipboard } from "../copying/clipboard.js";
import { readCardListFilters, describeFilters } from "../card_list_filter.js";
import { showToastMessage } from "../toast_message.js";

// The heading of the page the list sits on: a set's or character's name, "Your Collection", etc.
function listTitle(root){
    const titled = root.closest('[data-copy-title]');
    if (titled){
        return titled.dataset.copyTitle;
    }
    const heading = document.querySelector('main h1, main h2');
    return heading ? heading.textContent.trim() : "";
}

// Only the groups and cards the current filters leave visible
export function readCopyGroups(root){
    return [...root.querySelectorAll('.card-group-item')].filter(group => !group.hidden).map(group => {
        const cards = [...group.querySelectorAll('.card-rows > .card-row')].filter(row => !row.hidden).map(row => {
            const number = row.querySelector('.card-row-num')?.textContent.trim() || "";
            const copyName = (row.dataset.copyName || "").trim();
            return {
                number,
                // data-copy-name is "number: name", where name includes the card group on set-level lists
                name: copyName.startsWith(`${number}: `) ? copyName.slice(number.length + 2) : copyName,
                owned: Number(row.dataset.bsOwned) || 0,
                trade: Number(row.dataset.bsTrade) || 0,
                wanted: Number(row.dataset.bsWanted) || 0
            };
        });
        return { title: group.querySelector('.card-group-title').textContent.trim(), cards };
    }).filter(group => group.cards.length > 0);
}

function cardNotes(card, filters){
    // A filter already says what every card in the list has, so a single copy of that mark goes unsaid
    const note = (count, word, implied) => {
        if (count === 0 || (count === 1 && implied)){
            return null;
        }
        return count === 1 ? word : `${word} ${count}`;
    };
    return [
        note(card.owned, "have", filters.owned === "owned"),
        note(card.trade, "trade", filters.trade === "yes"),
        note(card.wanted, "want", filters.wanted === "yes")
    ].filter(Boolean);
}

export function formatCardListText({ title, filters, groups, showCounts = true }){
    const cardCount = groups.reduce((sum, group) => sum + group.cards.length, 0);
    const filterText = describeFilters(filters);
    const lines = [];
    if (title){
        lines.push(title);
    }
    const countText = `${cardCount} card${cardCount === 1 ? "" : "s"}`;
    lines.push(filterText ? `Filters: ${filterText} · ${countText}` : countText);

    groups.forEach(group => {
        lines.push("", `${group.title} (${group.cards.length})`);
        group.cards.forEach(card => {
            const notes = showCounts ? cardNotes(card, filters) : [];
            lines.push(`${card.number}: ${card.name}${notes.length ? ` — ${notes.join(", ")}` : ""}`);
        });
    });
    return lines.join("\n");
}

// Tabs and line breaks would split a cell, so they become spaces
const cell = (value) => String(value).replace(/[\t\r\n]+/g, " ");

export function formatCardListTsv({ groups, showCounts = true }){
    const header = ["Group", "Number", "Name"].concat(showCounts ? ["Owned", "For trade", "Wanted"] : []);
    const rows = groups.flatMap(group => group.cards.map(card =>
        [group.title, card.number, card.name].concat(showCounts ? [card.owned, card.trade, card.wanted] : [])
    ));
    return [header, ...rows].map(row => row.map(cell).join("\t")).join("\n");
}

export async function copyCardList(root, format, { isPublic = false } = {}){
    const groups = readCopyGroups(root);
    const showCounts = !isPublic;
    const text = format === "tsv"
        ? formatCardListTsv({ groups, showCounts })
        : formatCardListText({ title: listTitle(root), filters: readCardListFilters(root), groups, showCounts });
    const cardCount = groups.reduce((sum, group) => sum + group.cards.length, 0);

    if (await copy_to_clipboard(text)){
        showToastMessage(`Copied ${cardCount} card${cardCount === 1 ? "" : "s"} to clipboard`);
    }
    else {
        showToastMessage("Error copying cards to clipboard", "danger");
    }
    return text;
}
